import { Button } from '@orbitalwatch/design';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './icons';
import { SystemMessage } from './SystemMessage';

export interface NotFoundStateProps {
    /** The address that was asked for (default: none shown). */
    requested?: string;
    /** Overrides the default sentence (a page that is gone vs. a space that is gone). */
    text?: ReactNode;
    /** Where "Back" goes. Default `/spaces` ("Back to Spaces"). */
    backTo?: string;
    backLabel?: string;
}

/** The real Not found (Shell-NotFound): "404 · Not found", hidden means absent, Back to Spaces. */
export function NotFoundState(props: NotFoundStateProps) {
    const navigate = useNavigate();
    return (
        <SystemMessage
            eyebrow="404 · Not found"
            title="There is nothing at this address"
            tabTitle="Not found"
            text={props.text ?? 'The page may have moved, been deleted, or you may not be allowed to see it: hidden means absent.'}
            requested={props.requested}
            actions={<Button type="primary" text={props.backLabel ?? 'Back to Spaces'} onClick={() => navigate(props.backTo ?? '/spaces')} />}
        />
    );
}

export interface IdentityUnavailableStateProps {
    /** Try the call again (reload the page / the tree). */
    onRetry?: () => void;
    /** Overrides the default sentence. */
    text?: ReactNode;
}

/**
 * Identity is not answering, so Ark cannot tell which locked pages the user may read (the API answers 503 instead of
 * guessing). The Shell-Forbidden anatomy with the lock glyph, "Try again" and Back to Spaces.
 */
export function IdentityUnavailableState(props: IdentityUnavailableStateProps) {
    const navigate = useNavigate();
    return (
        <SystemMessage
            eyebrow="503 · Identity unavailable"
            title="Ark cannot check page locks right now"
            tabTitle="Identity unavailable"
            icon={<Icons.lock size={22} />}
            text={props.text ?? 'Locked pages are only shown once Identity confirms who may read them. Identity is not answering; try again in a moment.'}
            actions={<>
                {props.onRetry ? <Button type="primary" text="Try again" icon={<Icons.refresh size={16} />} onClick={props.onRetry} /> : null}
                <Button type={props.onRetry ? 'secondary' : 'primary'} text="Back to Spaces" onClick={() => navigate('/spaces')} />
            </>}
        />
    );
}
